import {
  type AcquireMultipleBookHandoffData,
  type PictureResponse,
  type AllMattersAnalyzeOptions,
} from "@/types/acquirebook";
import { type BookRequestCheckboxStatuses } from "../types";
import { useApiClient } from "@/hooks/useApiClient";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { makePictureRequestPayload } from "../utils/pictureRequest";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import {
  getSalesforceMapSearchId,
  removeSalesforceMapSearchId,
} from "@/features/salesforceIntegration/utils/handoffStorage";

interface AcquireBookForm {
  handleSubmit: (
    rows: AcquireMultipleBookHandoffData[],
    checkBoxOptions: BookRequestCheckboxStatuses[],
    pastDays: number | "",
    withOwnerAnalyze: boolean,
    withAllMattersAnalyze: boolean,
    reuseOnlyOwn: boolean,
    identifierName: string,
    cancelLoading: () => void,
    setDisableConfirmModalCancelButton?: React.Dispatch<
      React.SetStateAction<boolean>
    >,
    allMattersAnalyzeOptions?: AllMattersAnalyzeOptions
  ) => void;
}

export const useAcquireBookForm = (): AcquireBookForm => {
  const { apiClient } = useApiClient();
  const { acquiredRealEstateSearch } = useFeatureFlags();
  const navigate = useNavigate();

  const handleSubmit = (
    rows: AcquireMultipleBookHandoffData[],
    checkBoxOptions: BookRequestCheckboxStatuses[],
    pastDays: number | "",
    withOwnerAnalyze: boolean,
    withAllMattersAnalyze: boolean,
    reuseOnlyOwn: boolean,
    identificationName: string,
    cancelLoading: () => void,
    setDisableConfirmModalCancelButton?: React.Dispatch<
      React.SetStateAction<boolean>
    >,
    allMattersAnalyzeOptions?: AllMattersAnalyzeOptions
  ): void => {
    const req = makePictureRequestPayload(
      rows,
      checkBoxOptions,
      pastDays,
      withOwnerAnalyze,
      withAllMattersAnalyze,
      identificationName,
      allMattersAnalyzeOptions,
      getSalesforceMapSearchId()
    );

    if (setDisableConfirmModalCancelButton) {
      setDisableConfirmModalCancelButton(true);
    }

    const url = reuseOnlyOwn
      ? "/picture-request/acquire-reuse"
      : "/picture-request/";

    // 送信する
    apiClient
      .post<PictureResponse>(url, req)
      .then((_res) => {
        // 1回限りの引き継ぎ値のため、送信できた時点で破棄する。
        // 失敗時は残して再送信で引き継げるようにする（紐づけは後から復旧できない）
        removeSalesforceMapSearchId();
        navigate(
          acquiredRealEstateSearch === true
            ? "/acquirebooklist?view=requests"
            : "/acquirebooklist"
        );
      })
      .catch((e) => {
        if (
          isAxiosError(e) &&
          e.response?.status === HttpStatusCode.Forbidden
        ) {
          toast.error(READ_ONLY_TOOLTIP_MESSAGE);
          cancelLoading();
          return;
        }

        if (isAxiosError(e) && e.response && e.response.data) {
          /* eslint-disable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment */
          if (e.response.data.nonFieldErrors?.length) {
            // エラー文字列が配列なので複数のエラー文字列が返されても表示できるようにする
            const errorList: React.ReactNode[] =
              e.response.data.nonFieldErrors.map(
                (value: string): React.ReactNode => {
                  return (
                    <>
                      {value}
                      <br />
                    </>
                  );
                }
              );
            toast.error(<>{errorList}</>);
          }
          /* eslint-enable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment */
        }
        if (
          isAxiosError(e) &&
          (e.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          // Exclude Validation-Error(400)
          Sentry.captureException(e);
        }
        cancelLoading();
        console.error(e);
      })
      .finally(() => {
        if (setDisableConfirmModalCancelButton) {
          setDisableConfirmModalCancelButton(false);
        }
      });
  };

  return {
    handleSubmit,
  };
};
